"use client";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HouseRule } from "@/lib/types";
import { rise } from "@/lib/animation/variant";

interface HouseRuleRowProps {
  rule: HouseRule;
  index: number;
  active: boolean;
  onHover: (index: number | null) => void;
}

// One rule, at headline scale. Dimmed until pointed at, so the list reads as a
// spotlight moving down the page rather than four things shouting at once.
//
// A real <Link>, always in the markup — these are the only links from the home
// page into three of the four case studies, and hiding them behind hover would
// make them invisible to a crawler.
const HouseRuleRow = ({ rule, index, active, onHover }: HouseRuleRowProps) => (
  <motion.div
    variants={rise}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.6 }}
    custom={index}
    onMouseEnter={() => onHover(index)}
    onMouseLeave={() => onHover(null)}
    className="group/rule relative border-b border-white/10"
  >
    <Link
      href={rule.href}
      // Right padding is the slack the hover shift moves into. Without it the
      // trailing arrow slid past the container edge and off the screen —
      // visible on touch too, where a tap counts as a hover.
      className="relative block py-8 pr-8 md:py-10 md:pr-10"
      aria-label={`${rule.rule} — ${rule.proof}`}
    >
      {/* Lime sweep that fills from the left on hover. */}
      <motion.span
        aria-hidden
        initial={false}
        animate={{ scaleX: active ? 1 : 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left bg-primary"
      />

      <motion.div
        animate={{ x: active ? 24 : 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 26 }}
        className="flex items-start gap-5 md:gap-8"
      >
        <span
          className={`mt-2 shrink-0 text-[11px] font-black tracking-[0.3em] transition-colors duration-300 ${
            active ? "text-primary" : "text-white/25"
          }`}
        >
          {rule.number}
        </span>

        <span className="min-w-0 flex-1">
          <span
            className={`block text-3xl font-black uppercase leading-[0.95] tracking-tighter transition-colors duration-300 md:text-5xl lg:text-6xl ${
              active ? "text-white" : "text-white/35"
            }`}
          >
            {rule.rule}
          </span>

          {/* Always present on desktop, revealed with the row. On touch it is
              simply always readable — there is no hover to wait for. */}
          <motion.span
            initial={false}
            animate={{
              opacity: active ? 1 : 0,
              y: active ? 0 : -6,
            }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 hidden max-w-xl text-sm leading-relaxed text-white/50 lg:block"
          >
            {rule.detail}
          </motion.span>
          <span className="mt-3 block max-w-xl text-sm leading-relaxed text-white/50 lg:hidden">
            {rule.detail}
          </span>

          {/* Touch fallback for the cursor preview. */}
          <span className="mt-5 flex items-center gap-3 lg:hidden">
            <span className="relative size-14 shrink-0 overflow-hidden rounded-xl border border-white/10">
              <Image
                src={rule.image}
                alt=""
                fill
                sizes="56px"
                className="object-cover"
              />
            </span>
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
              {rule.proof}
            </span>
          </span>
        </span>

        <motion.span
          aria-hidden
          animate={{
            opacity: active ? 1 : 0.25,
            rotate: active ? 0 : -35,
          }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className={`mt-1 grid size-11 shrink-0 place-items-center rounded-full border transition-colors duration-300 md:size-12 ${
            active
              ? "border-primary bg-primary text-black"
              : "border-white/15 text-white/50"
          }`}
        >
          <ArrowUpRight size={18} />
        </motion.span>
      </motion.div>
    </Link>
  </motion.div>
);

export default HouseRuleRow;
